import { Button } from "../components/Button";
import { ImageGenerationPlaceholder } from "../components/ImageGenerationPlaceholder";
import { ImageWrapper } from "../components/ImageWrapper";
import { downloadFile, ImageZoomDialog, } from "../components/ImageZoomDialog";
import { Spinner } from "../components/Spinner";
import { Download01, XClose } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import React, { useCallback, useState } from "react";
export const IMAGE_PREVIEW_VARIANTS = ["embedded", "standalone"];
export const IMAGE_PREVIEW_TITLE_POSITIONS = ["bottom", "center"];
const containerVariants = cva(cn("s-cursor-pointer s-overflow-hidden s-rounded-2xl"), {
    variants: {
        variant: {
            // Embedded inside a parent component (like Citation) that provides the group
            embedded: "s-absolute s-inset-0",
            // Standalone, self-contained component managing its own hover
            standalone: cn("s-group/image-preview", "s-relative s-aspect-square", "s-bg-muted-background dark:s-bg-muted-background-night"),
        },
    },
    defaultVariants: {
        variant: "embedded",
    },
});
const overlayVariants = cva(cn("s-absolute s-inset-0 s-z-10", "s-bg-primary-100/60 dark:s-bg-primary-100-night/60", "s-opacity-0 s-transition s-duration-200"), {
    variants: {
        titlePosition: {
            bottom: cn("s-flex s-flex-col s-items-start s-justify-end", "s-px-3 s-pb-7"),
            center: "s-flex s-items-center s-justify-center",
        },
        variant: {
            // Embedded: uses parent's s-group for hover
            embedded: "group-hover:s-opacity-100",
            // Standalone: uses its own s-group/image-preview
            standalone: "group-hover/image-preview:s-opacity-100",
        },
    },
    defaultVariants: {
        titlePosition: "bottom",
        variant: "embedded",
    },
});
const titleVariants = cva(cn("s-max-w-full s-truncate", "s-heading-sm", "s-text-foreground dark:s-text-foreground-night"), {
    variants: {
        titlePosition: {
            bottom: "",
            center: "s-max-w-[90%] s-px-2 s-text-center",
        },
    },
    defaultVariants: {
        titlePosition: "bottom",
    },
});
const ImagePreview = React.forwardRef(({ imgSrc, alt = "", title = "", downloadUrl, isLoading, isGenerating, onClose, onClick, className, variant = "embedded", titlePosition = "bottom", manageZoomDialog = true, }, ref) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const handleDownload = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        if (downloadUrl && title) {
            downloadFile(downloadUrl, title);
        }
    }, [downloadUrl, title]);
    const handleClose = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        onClose === null || onClose === void 0 ? void 0 : onClose(e);
    }, [onClose]);
    const handleImageClick = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        if (!isLoading && !isGenerating) {
            if (onClick) {
                onClick(e);
            }
            else if (manageZoomDialog) {
                setIsDialogOpen(true);
            }
        }
    }, [isLoading, isGenerating, onClick, manageZoomDialog]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: ref, onClick: handleImageClick, className: cn(containerVariants({ variant }), className) }, isGenerating ? (React.createElement(ImageGenerationPlaceholder, { fill: true })) : isLoading ? (React.createElement("div", { className: cn("s-flex s-h-full s-w-full s-items-center s-justify-center", "s-bg-muted-background dark:s-bg-muted-background-night") },
            React.createElement(Spinner, { variant: "dark", size: "md" }))) : (React.createElement(React.Fragment, null,
            React.createElement(ImageWrapper, { src: imgSrc, alt: alt, className: cn("s-h-full s-w-full s-object-cover s-transition s-duration-200", variant === "embedded"
                    ? "group-hover:s-blur-sm"
                    : "group-hover/image-preview:s-blur-sm") }),
            React.createElement("div", { className: overlayVariants({ titlePosition, variant }) },
                React.createElement("span", { className: titleVariants({ titlePosition }) }, title)),
            React.createElement("div", { className: cn("s-absolute s-right-2 s-top-2 s-z-20", "s-opacity-0 s-transition-opacity s-duration-200", variant === "embedded"
                    ? "group-hover:s-opacity-100"
                    : "group-hover/image-preview:s-opacity-100") },
                onClose && (React.createElement(Button, { variant: "ghost", size: "mini", icon: XClose, tooltip: "Remove", onClick: handleClose })),
                !onClose && downloadUrl && (React.createElement(Button, { variant: "ghost", size: "mini", icon: Download01, tooltip: "Download", onClick: handleDownload })))))),
        manageZoomDialog && (React.createElement(ImageZoomDialog, { open: isDialogOpen, onOpenChange: setIsDialogOpen, image: {
                src: imgSrc,
                alt,
                title,
                downloadUrl,
                isLoading,
            } }))));
});
ImagePreview.displayName = "ImagePreview";
export { ImagePreview };
//# sourceMappingURL=ImagePreview.js.map